import React, { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { Card, CardAction } from '@atlaskit/media-card';
import { FileIdentifier } from '@atlaskit/media-client';
import { MediaClientConfig } from '@atlaskit/media-core';
import { FilmstripView } from '@atlaskit/media-filmstrip';

import { useMediaIdContext } from 'src/contexts/media-id';

import messages from './i18n';
import MediaViewerWithMetadataPanel from './media-viewer-with-metadata';
import * as styles from './styles';
import { Attachment, CollectionName } from './types';
import { buildMediaIdentifiers, findByIdentifier } from './utils';

type ViewOptions = {
  animate: boolean;
  offset: number;
};

export type Props = {
  attachments: Attachment[];
  collectionName: CollectionName;
  mediaClientConfig: MediaClientConfig;
  pendingIds?: string[];
  // returns list of available actions for the specific attachment
  // actions can vary and are specific to each attachment
  getCardActions?: (attachment: Attachment) => CardAction[];
};

function MediaAttachmentFilmstrip(props: Props) {
  const {
    attachments,
    collectionName,
    mediaClientConfig,
    pendingIds = [],
    getCardActions,
  } = props;

  const [identifier, setIdentifier] = useState<FileIdentifier | undefined>();
  const [viewOptions, setViewOptions] = useState<ViewOptions>({
    offset: 0,
    animate: false,
  });

  const mediaIdentifiers = buildMediaIdentifiers({
    attachments,
    collectionName,
  });

  const { mediaId, setMediaId } = useMediaIdContext();

  useEffect(() => {
    if (!mediaId) {
      setIdentifier(undefined);
      return;
    }

    // Set identifier from context when applicable.
    // This assumes the incoming UUID is valid and part of the current collection
    setIdentifier({
      collectionName,
      id: mediaId,
      mediaItemType: 'file',
    });
  }, [collectionName, mediaId]);

  const dataSource = { list: mediaIdentifiers };

  const onOpen = (selected: FileIdentifier) => setMediaId(selected.id);
  const onClose = () => setMediaId(undefined);

  const onScroll = ({ animate, offset }: ViewOptions) => {
    setViewOptions({ animate, offset });
  };

  const onSize = useCallback(
    ({ offset }: { offset: number }) => {
      setViewOptions({ ...viewOptions, offset });
    },
    [viewOptions]
  );

  const noAttachments =
    (!mediaIdentifiers || mediaIdentifiers.length === 0) &&
    (!pendingIds || pendingIds.length === 0);

  return (
    <styles.FilmstripContainer>
      {noAttachments ? (
        <styles.FlexContainer>
          <FormattedMessage {...messages.noAttachments} />
        </styles.FlexContainer>
      ) : (
        <FilmstripView
          animate={viewOptions.animate}
          offset={viewOptions.offset}
          onScroll={onScroll}
          onSize={onSize}
        >
          {mediaIdentifiers &&
            mediaIdentifiers.map((fileID: FileIdentifier, index: number) => (
              <Card
                key={index}
                identifier={fileID}
                mediaClientConfig={mediaClientConfig}
                onClick={() => onOpen(fileID)}
                actions={
                  getCardActions ? getCardActions(attachments[index]) : []
                }
              />
            ))}
          {pendingIds &&
            pendingIds.map((pendingId: string, index: number) => (
              <Card
                key={index + mediaIdentifiers.length}
                identifier={{
                  id: pendingId,
                  collectionName,
                  mediaItemType: 'file',
                }}
                mediaClientConfig={mediaClientConfig}
              />
            ))}
        </FilmstripView>
      )}
      {!!identifier && (
        <MediaViewerWithMetadataPanel
          getAttachmentData={id =>
            findByIdentifier({ attachments, identifier: id })
          }
          mediaClientConfig={mediaClientConfig}
          identifier={identifier}
          collectionName={collectionName}
          onClose={onClose}
          dataSource={dataSource}
        />
      )}
    </styles.FilmstripContainer>
  );
}

export default MediaAttachmentFilmstrip;
